import { useEffect, useState, useCallback } from 'react'
import { listTags, createTag, renameTag, deleteTag, mergeTag, listEvents } from '../lib/api.js'
import { slugify } from '../components/TagPicker.jsx'
import ErrorAlert from '../components/ErrorAlert.jsx'
import { PAGE_STANDARD } from '../lib/layout.js'
import { formatLocalDateTime } from '../lib/datetime.js'
import { BTN_COMPACT_PRIMARY, BTN_PRIMARY, INPUT } from '../lib/ui.js'


// Tag management surface (#116 create, #294 rename/delete/merge). Lists every
// tag with its upcoming-published-event usage count and lets an admin create,
// rename (in place), delete (soft, with confirm), merge into another tag, and
// drill into the events carrying a tag. POST /api/v1/tags is idempotent on
// slug collision; the rename/delete/merge endpoints are ROLE_ADMIN-only.
export default function AdminTagsPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [adding, setAdding] = useState(false)
  const [name, setName] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Per-row interaction state.
  const [mergingId, setMergingId] = useState(null)
  const [mergeTargetId, setMergeTargetId] = useState('')
  const [expandedId, setExpandedId] = useState(null)
  const [eventsByTag, setEventsByTag] = useState({}) // slug -> { loading, events, error }

  const load = useCallback(() => {
    setLoading(true)
    setError(null)
    listTags()
      .then(setRows)
      .catch((err) => setError(err.message || String(err)))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const previewSlug = slugify(name)

  const submitNew = async (e) => {
    e.preventDefault()
    setError(null)
    const trimmed = name.trim()
    if (!trimmed) { setError('Tag name is required.'); return }
    if (!previewSlug) { setError('Tag name must contain at least one letter or digit.'); return }
    setSubmitting(true)
    try {
      await createTag(trimmed)
      setName('')
      setAdding(false)
      load()
    } catch (err) {
      setError(err.message || String(err))
    } finally {
      setSubmitting(false)
    }
  }

  const rename = async (tag, nextName) => {
    const trimmed = nextName.trim()
    if (!trimmed || trimmed === tag.name) return
    setError(null)
    try {
      const updated = await renameTag(tag.id, trimmed)
      setRows((prev) => prev.map((r) => (r.id === tag.id ? updated : r)))
    } catch (err) {
      setError(err.message || String(err))
      load() // re-sync the input to the server value on failure
    }
  }

  const remove = async (tag) => {
    const warn = tag.usageCount > 0
      ? ` It's on ${tag.usageCount} upcoming event${tag.usageCount === 1 ? '' : 's'} — it will be removed from ${tag.usageCount === 1 ? 'it' : 'them'}.`
      : ''
    if (!window.confirm(`Delete the "${tag.name}" tag?${warn}`)) return
    setError(null)
    try {
      await deleteTag(tag.id)
      load()
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const startMerge = (tag) => {
    setExpandedId(null)
    setMergingId(tag.id)
    setMergeTargetId('')
  }

  const confirmMerge = async (source) => {
    if (!mergeTargetId) return
    setError(null)
    try {
      await mergeTag(source.id, mergeTargetId)
      setMergingId(null)
      setMergeTargetId('')
      load()
    } catch (err) {
      setError(err.message || String(err))
    }
  }

  const toggleDrill = async (tag) => {
    setMergingId(null)
    if (expandedId === tag.id) { setExpandedId(null); return }
    setExpandedId(tag.id)
    if (!eventsByTag[tag.slug]) {
      setEventsByTag((m) => ({ ...m, [tag.slug]: { loading: true, events: [], error: null } }))
      try {
        const page = await listEvents({ tag: tag.slug })
        const events = Array.isArray(page) ? page : (page.content || [])
        setEventsByTag((m) => ({ ...m, [tag.slug]: { loading: false, events, error: null } }))
      } catch (err) {
        setEventsByTag((m) => ({ ...m, [tag.slug]: { loading: false, events: [], error: err.message || String(err) } }))
      }
    }
  }

  return (
    <main className={`${PAGE_STANDARD} pb-12 pt-8 sm:pt-10`}>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            <span className="brand-gradient-text">Tags</span>
          </h1>
          <p className="mt-2 text-base text-slate-600">
            Topic tags speakers attach to events. Rename, merge, or delete them here;
            the count is upcoming, published events carrying each tag — click it to see which.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAdding((v) => !v)}
          className={BTN_PRIMARY}
        >
          {adding ? 'Cancel' : 'Add tag'}
        </button>
      </header>

      {error ? <ErrorAlert title="Tag management error" message={error} source="AdminTagsPage" /> : null}

      {adding ? (
        <form onSubmit={submitNew} className="mb-4 flex flex-wrap items-end gap-3 rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/60">
          <label className="text-sm flex-1 min-w-[220px]">
            <span className="block text-xs font-medium text-slate-600">Tag name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={`mt-1 w-full ${INPUT}`}
              placeholder="e.g. Artificial Intelligence"
              maxLength={80}
              autoFocus
              required
            />
            <span className="mt-1 block text-xs text-slate-500">
              {previewSlug ? <>Slug: <code>{previewSlug}</code></> : 'The slug is generated from the name.'}
            </span>
          </label>
          <button
            type="submit"
            disabled={submitting}
            className={BTN_PRIMARY}
          >
            {submitting ? 'Adding…' : 'Add'}
          </button>
        </form>
      ) : null}

      {loading ? (
        <div className="rounded-2xl bg-white p-8 text-center text-slate-500 shadow-sm ring-1 ring-slate-200/60">
          Loading…
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl bg-white p-10 text-center shadow-sm ring-1 ring-slate-200/60">
          <h3 className="text-base font-semibold text-slate-900">No tags yet</h3>
          <p className="mt-1 text-sm text-slate-500">Add one to start tagging events.</p>
        </div>
      ) : (
        /* #356: `overflow-hidden` CLIPPED the table at narrow widths, so the
           right-hand columns — which is where the actions live — were simply
           unreachable on a phone. `overflow-x-auto` plus an explicit min-width
           makes the table scroll inside its own card instead, which also keeps
           the page itself from scrolling sideways. */
        <div className="overflow-x-auto rounded-2xl bg-white shadow-sm ring-1 ring-slate-200/60">
          <table className="min-w-[680px] w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Slug</th>
                <th className="px-4 py-2 text-right">Upcoming events</th>
                <th className="px-4 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((t) => {
                const others = rows.filter((r) => r.id !== t.id)
                const drill = eventsByTag[t.slug]
                return (
                  <RowGroup key={t.id || t.slug}>
                    <tr>
                      <td className="px-4 py-3 align-top">
                        <input
                          type="text"
                          defaultValue={t.name}
                          maxLength={80}
                          onBlur={(e) => rename(t, e.target.value)}
                          aria-label={`Rename ${t.name}`}
                          className={INPUT + ' w-52'}
                        />
                      </td>
                      <td className="px-4 py-3 align-top">
                        <code className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700">{t.slug}</code>
                      </td>
                      <td className="px-4 py-3 align-top text-right tabular-nums">
                        {t.usageCount > 0 ? (
                          <button
                            type="button"
                            onClick={() => toggleDrill(t)}
                            className="font-medium text-brand-700 hover:underline"
                            aria-expanded={expandedId === t.id}
                          >
                            {t.usageCount}
                          </button>
                        ) : (
                          <span className="text-slate-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-right">
                        <div className="inline-flex items-center gap-1">
                          <button type="button" onClick={() => startMerge(t)}
                                  disabled={others.length === 0}
                                  title={others.length === 0 ? 'No other tag to merge into' : 'Merge into another tag'}
                                  className="rounded-full px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40">
                            Merge
                          </button>
                          <button type="button" onClick={() => remove(t)}
                                  className="rounded-full px-3 py-1 text-xs font-medium text-rose-700 hover:bg-rose-50">
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>

                    {mergingId === t.id ? (
                      <tr className="bg-slate-50/60">
                        <td colSpan={4} className="px-4 py-3">
                          <div className="flex flex-wrap items-center gap-2 text-sm">
                            <span className="text-slate-600">Merge <strong>{t.name}</strong> into</span>
                            <select value={mergeTargetId} onChange={(e) => setMergeTargetId(e.target.value)}
                                    className={INPUT}>
                              <option value="">Select a tag…</option>
                              {others.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                            </select>
                            <button type="button" onClick={() => confirmMerge(t)} disabled={!mergeTargetId}
                                    className={BTN_COMPACT_PRIMARY}>
                              Merge
                            </button>
                            <button type="button" onClick={() => { setMergingId(null); setMergeTargetId('') }}
                                    className="inline-flex items-center rounded-full px-3 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-100">
                              Cancel
                            </button>
                            <span className="text-xs text-slate-500">
                              Events on <strong>{t.name}</strong> move to the target; <strong>{t.name}</strong> is then deleted.
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : null}

                    {expandedId === t.id ? (
                      <tr className="bg-slate-50/60">
                        <td colSpan={4} className="px-4 py-3">
                          {!drill || drill.loading ? (
                            <div className="text-sm text-slate-500">Loading events…</div>
                          ) : drill.error ? (
                            <div className="text-sm text-rose-700">{drill.error}</div>
                          ) : drill.events.length === 0 ? (
                            <div className="text-sm text-slate-500">No upcoming published events carry this tag.</div>
                          ) : (
                            <ul className="space-y-1">
                              {drill.events.map((e) => (
                                <li key={e.id} className="text-sm text-slate-700">
                                  <span className="font-medium">{e.title}</span>
                                  <span className="text-slate-400"> — {formatLocalDateTime(e.startsAtUtc)}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    ) : null}
                  </RowGroup>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </main>
  )
}

// Fragment wrapper so each tag can render its main row plus optional
// merge / drill-down sub-rows under a single key.
function RowGroup({ children }) {
  return <>{children}</>
}
